import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { MeView } from '@vit/contracts';
import type { z } from 'zod';
import type { RuntimeConfigView } from '@vit/contracts';
import { api, queryClient, setCsrfToken } from './api.js';
import { applyLocale } from './i18n.js';
import { apiUrl, fetchCredentials } from './api-base.js';
import { setDisplayTimezone } from './format.js';

type RuntimeConfig = z.infer<typeof RuntimeConfigView>;

interface SessionState {
  status: 'loading' | 'authenticated' | 'anonymous' | 'error';
  me: MeView | null;
  runtime: RuntimeConfig | null;
  refreshMe: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function loginUrl(returnTo = window.location.pathname + window.location.search) {
  return apiUrl(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
}

/** Loads the server session (HttpOnly cookie), the CSRF token and the profile. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<SessionState, 'refreshMe' | 'signOut'>>({ status: 'loading', me: null, runtime: null });

  const refreshMe = async () => {
    const me = await api.call('getMe', {});
    applyLocale(me!.data.locale);
    setDisplayTimezone(me!.data.timezone);
    setState((s) => ({ ...s, me: me!.data, status: 'authenticated' }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Transient failures (API restarting, network blip) retry before showing an error.
      for (let attempt = 0; attempt < 4 && !cancelled; attempt++) {
        if (attempt) await new Promise((r) => setTimeout(r, 750 * attempt));
      try {
        const [sessionResponse, runtime] = await Promise.all([
          fetch(apiUrl('/auth/session'), { credentials: fetchCredentials, headers: { Accept: 'application/json' } }).then((r) => r.json() as Promise<{ authenticated: boolean; csrfToken?: string }>),
          api.call('getRuntimeConfig', {}),
        ]);
        if (cancelled) return;
        if (!sessionResponse.authenticated) {
          setState({ status: 'anonymous', me: null, runtime: runtime!.data as RuntimeConfig });
          return;
        }
        setCsrfToken(sessionResponse.csrfToken ?? null);
        const me = await api.call('getMe', {});
        if (cancelled) return;
        applyLocale(me!.data.locale);
        setDisplayTimezone(me!.data.timezone);
        setState({ status: 'authenticated', me: me!.data, runtime: runtime!.data as RuntimeConfig });
        return;
      } catch {
        // retry below
      }
      }
      if (!cancelled) setState((s) => ({ ...s, status: 'error' }));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signOut = async () => {
    const session = await fetch(apiUrl('/auth/session'), { credentials: fetchCredentials }).then((r) => r.json() as Promise<{ csrfToken?: string }>);
    await fetch(apiUrl('/auth/logout'), { method: 'POST', credentials: fetchCredentials, headers: { 'X-CSRF-Token': session.csrfToken ?? '' } });
    setCsrfToken(null);
    // Frontend caches clear on logout/user change (Section 14.1).
    queryClient.clear();
    window.location.assign('/signed-out');
  };

  return <SessionContext.Provider value={{ ...state, refreshMe, signOut }}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

/** Convenience accessor for the authenticated profile. */
export function useMe(): MeView {
  const { me } = useSession();
  if (!me) throw new Error('useMe requires an authenticated session');
  return me;
}
